/**
 * IntegrationSelectionCard - Card for selecting an integration in the Add modal
 */

import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import type { IntegrationDetails, Language } from '@/types/integrations'
import { getLocalizedText } from '@/lib/integrations/integrationUtils'

interface IntegrationSelectionCardProps {
  integrationKey: string
  details: IntegrationDetails
  isSelected: boolean
  onClick: () => void
  disabled?: boolean
  isLive?: boolean
  language: Language
}

export function IntegrationSelectionCard({
  details,
  isSelected,
  onClick,
  disabled,
  isLive,
  language,
}: IntegrationSelectionCardProps) {
  const { t } = useTranslation()
  const displayName = getLocalizedText(details.displayName, language)
  const description = getLocalizedText(details.description, language)

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled && !isLive}
      className={cn(
        'relative flex flex-col items-center rounded-lg border-2 p-4 text-center transition-all',
        // Base styles
        'bg-white dark:bg-gray-800',
        // Border styles based on state
        isSelected
          ? 'border-primary ring-2 ring-primary/20'
          : isLive
            ? 'border-green-500'
            : 'border-gray-200 dark:border-gray-700',
        // Hover/disabled states
        disabled && !isLive
          ? 'cursor-not-allowed opacity-60'
          : 'cursor-pointer hover:-translate-y-0.5 hover:shadow-md',
        // Focus styles
        'focus:outline-none focus:ring-2 focus:ring-primary/50'
      )}
    >
      {/* Active/Beta/Selected Badge */}
      {(isLive || details.status === 'beta' || (isSelected && !isLive)) && (
        <div className="absolute right-3 top-3 flex items-center gap-1">
          {isLive ? (
            <span className="rounded-full bg-green-500 px-2 py-0.5 text-xs font-semibold text-white">
              {t('resources.assistants.integrations.card.status_active')}
            </span>
          ) : details.status === 'beta' ? (
            <span className="rounded-full bg-amber-500 px-2 py-0.5 text-xs font-semibold text-white">
              {t('resources.assistants.integrations.card.status_beta')}
            </span>
          ) : isSelected ? (
            <>
              <span className="h-2 w-2 animate-pulse rounded-full bg-primary" />
              <span className="rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-white">
                {t('resources.assistants.integrations.card.status_selected')}
              </span>
            </>
          ) : null}
        </div>
      )}

      {/* Icon */}
      <div className="mb-3 flex h-16 w-16 items-center justify-center">
        <img
          src={details.iconPath}
          alt={displayName}
          className={cn(
            'h-12 w-12 object-contain transition-all',
            (isSelected || isLive) && 'brightness-110'
          )}
        />
      </div>

      {/* Name */}
      <h3
        className={cn(
          'mb-1 text-base font-semibold transition-colors',
          isSelected
            ? 'text-primary'
            : isLive
              ? 'text-green-600 dark:text-green-400'
              : 'text-gray-900 dark:text-gray-100'
        )}
      >
        {displayName}
      </h3>

      {/* Description */}
      <p className="text-sm leading-tight text-gray-500 dark:text-gray-400">
        {description}
      </p>
    </button>
  )
}
